/**
 * Los iconos del panel y del back office · 13-10-2026
 *
 * Trazo de 1.8 sobre 24 × 24, como la luna y el sol de `CambiarAspecto`, y color
 * del texto que lo rodea (`currentColor`): un icono no trae su propio color, lo
 * hereda del botón o de la pastilla donde está.
 *
 * Sin librería, a propósito: son unos treinta, el panel ya cuida mucho lo que pesa
 * y una dependencia de iconos trae cientos que nunca se usan. Siempre van con texto
 * al lado o con `aria-label` en su botón, así que aquí son decorativos
 * (`aria-hidden`).
 */

export type NombreDeIcono =
  | 'inicio'
  | 'alumnos'
  | 'calendario'
  | 'avisos'
  | 'cobros'
  | 'grupos'
  | 'grados'
  | 'informes'
  | 'pagadores'
  | 'club'
  | 'cumplimiento'
  | 'material'
  | 'competicion'
  | 'mas'
  | 'campana'
  | 'cuenta'
  | 'manual'
  | 'salir'
  | 'flecha'
  | 'alerta'
  | 'hecho'
  | 'mensaje'
  | 'contrato'
  | 'documento'
  | 'sede'
  | 'invitar'
  | 'reloj'
  | 'descuento'
  | 'remesa'
  | 'euro'
  | 'buscador'
  | 'clubes'
  | 'comercial'
  | 'soporte'
  | 'operacion'
  | 'catalogos'
  | 'directorio'
  | 'cerrar'
  | 'mas-uno'
  | 'escudo'
  | 'pulso'
  | 'luna'
  | 'sol';

const TRAZOS: Record<NombreDeIcono, React.ReactNode> = {
  inicio: (
    <>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V20a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V9.5" />
    </>
  ),
  alumnos: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M16 4.6a3.5 3.5 0 0 1 0 6.8" />
      <path d="M18 14.2a6.5 6.5 0 0 1 3.5 5.8" />
    </>
  ),
  calendario: (
    <>
      <rect x="3" y="4.5" width="18" height="16.5" rx="2" />
      <path d="M3 9.5h18M8 2.5v4M16 2.5v4" />
    </>
  ),
  avisos: (
    <>
      <path d="M4 10v4a1 1 0 0 0 1 1h2l6 4V5L7 9H5a1 1 0 0 0-1 1Z" />
      <path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12" />
    </>
  ),
  cobros: (
    <>
      <rect x="2.5" y="5.5" width="19" height="13" rx="2" />
      <path d="M2.5 10h19M6.5 14.5h4" />
    </>
  ),
  grupos: (
    <>
      <path d="m12 3 9 4.5-9 4.5-9-4.5L12 3Z" />
      <path d="m3 12 9 4.5 9-4.5M3 16.5 12 21l9-4.5" />
    </>
  ),
  grados: (
    <>
      <circle cx="12" cy="9" r="6" />
      <path d="m8.5 13.8-1.5 7.2 5-2.6 5 2.6-1.5-7.2" />
    </>
  ),
  informes: <path d="M5 20V12M10 20V6M15 20v-9M20 20V9M3 20.5h18" />,
  pagadores: (
    <>
      <path d="M3 7.5A2.5 2.5 0 0 1 5.5 5H18a1 1 0 0 1 1 1v2" />
      <path d="M3 7.5V18a2 2 0 0 0 2 2h14a1 1 0 0 0 1-1v-3" />
      <path d="M21 11h-4a2 2 0 0 0 0 4h4v-4Z" />
    </>
  ),
  club: (
    <>
      <path d="M4 21V9l8-5.5L20 9v12" />
      <path d="M9 21v-6h6v6M2.5 21h19" />
    </>
  ),
  cumplimiento: (
    <>
      <path d="M12 21.5s8-3.8 8-10V5.5L12 2.5 4 5.5v6c0 6.2 8 10 8 10Z" />
      <path d="m8.8 12 2.2 2.2 4.4-4.4" />
    </>
  ),
  material: (
    <>
      <path d="m12 2.5 8.5 4.8v9.4L12 21.5l-8.5-4.8V7.3L12 2.5Z" />
      <path d="M3.5 7.3 12 12l8.5-4.7M12 12v9.5" />
    </>
  ),
  competicion: (
    <>
      <path d="M8 21h8M12 17v4" />
      <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
      <path d="M7 6H4a3 3 0 0 0 3 4M17 6h3a3 3 0 0 1-3 4" />
    </>
  ),
  mas: (
    <>
      <circle cx="5" cy="12" r="1.3" />
      <circle cx="12" cy="12" r="1.3" />
      <circle cx="19" cy="12" r="1.3" />
    </>
  ),
  campana: (
    <>
      <path d="M18 8.5a6 6 0 1 0-12 0c0 6.5-2.5 8.5-2.5 8.5h17S18 15 18 8.5Z" />
      <path d="M10 20.5a2.2 2.2 0 0 0 4 0" />
    </>
  ),
  cuenta: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  manual: (
    <>
      <path d="M2.5 4.5H8a4 4 0 0 1 4 4V21a3 3 0 0 0-3-3H2.5V4.5Z" />
      <path d="M21.5 4.5H16a4 4 0 0 0-4 4V21a3 3 0 0 1 3-3h6.5V4.5Z" />
    </>
  ),
  salir: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5M21 12H9" />
    </>
  ),
  flecha: <path d="m9 18 6-6-6-6" />,
  alerta: (
    <>
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4.5M12 17h.01" />
    </>
  ),
  hecho: (
    <>
      <circle cx="12" cy="12" r="9.5" />
      <path d="m7.8 12.3 2.8 2.8 5.6-5.6" />
    </>
  ),
  mensaje: (
    <path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8v.5Z" />
  ),
  contrato: (
    <>
      <path d="M14 2.5H6a2 2 0 0 0-2 2v15a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8.5l-6-6Z" />
      <path d="M14 2.5v6h6M8 17.5c1.3-1.6 2.3-1.6 3 0s1.7 1.6 3 0" />
    </>
  ),
  documento: (
    <>
      <path d="M14 2.5H6a2 2 0 0 0-2 2v15a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8.5l-6-6Z" />
      <path d="M14 2.5v6h6M8.5 13h7M8.5 17h7" />
    </>
  ),
  sede: (
    <>
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </>
  ),
  invitar: (
    <>
      <circle cx="9" cy="8" r="4" />
      <path d="M2 21a7 7 0 0 1 14 0M19 8v6M22 11h-6" />
    </>
  ),
  reloj: (
    <>
      <circle cx="12" cy="12" r="9.5" />
      <path d="M12 6.5V12l3.5 2" />
    </>
  ),
  descuento: (
    <>
      <path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8Z" />
      <path d="M7.5 7.5h.01" />
    </>
  ),
  remesa: (
    <>
      <path d="M21.5 2.5 10.5 13.5" />
      <path d="m21.5 2.5-7 19-4-8.5-8.5-4 19.5-6.5Z" />
    </>
  ),
  euro: (
    <>
      <path d="M17.5 6.2A7 7 0 1 0 17.5 17.8" />
      <path d="M4 10h9M4 14h9" />
    </>
  ),
  buscador: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20.5 20.5-4.5-4.5" />
    </>
  ),
  clubes: (
    <>
      <rect x="4" y="2.5" width="16" height="19" rx="2" />
      <path d="M9.5 21.5V17h5v4.5M8.5 7h.01M12 7h.01M15.5 7h.01M8.5 11h.01M12 11h.01M15.5 11h.01" />
    </>
  ),
  comercial: (
    <>
      <path d="M3 17 9 11l4 4 8-8" />
      <path d="M15 7h6v6" />
    </>
  ),
  soporte: (
    <>
      <circle cx="12" cy="12" r="9.5" />
      <circle cx="12" cy="12" r="4" />
      <path d="m5.3 5.3 3.9 3.9M14.8 14.8l3.9 3.9M14.8 9.2l3.9-3.9M5.3 18.7l3.9-3.9" />
    </>
  ),
  operacion: <path d="M22 12h-4l-3 9-6-18-3 9H2" />,
  catalogos: (
    <>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2.5H20v19H6.5A2.5 2.5 0 0 1 4 19V5a2.5 2.5 0 0 1 2.5-2.5Z" />
    </>
  ),
  directorio: (
    <>
      <path d="M1.5 6v15.5L8 18l8 3.5L22.5 18V2.5L16 6 8 2.5 1.5 6Z" />
      <path d="M8 2.5V18M16 6v15.5" />
    </>
  ),
  cerrar: <path d="M18 6 6 18M6 6l12 12" />,
  'mas-uno': <path d="M12 5v14M5 12h14" />,
  escudo: <path d="M12 21.5s8-3.8 8-10V5.5L12 2.5 4 5.5v6c0 6.2 8 10 8 10Z" />,
  pulso: <path d="M22 12h-4l-3 9-6-18-3 9H2" />,
  luna: <path d="M20.5 14.3A8.5 8.5 0 0 1 9.7 3.5a8.5 8.5 0 1 0 10.8 10.8Z" />,
  sol: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4" />
    </>
  ),
};

export function Icono({
  nombre,
  tamano = 18,
  className,
}: {
  nombre: NombreDeIcono;
  /** En píxeles. 18 en botones y menús; 22 en la barra de abajo del móvil. */
  tamano?: number;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={tamano}
      height={tamano}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className ? `icono ${className}` : 'icono'}
    >
      {TRAZOS[nombre]}
    </svg>
  );
}
